import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";

// SPRINT 09 · Vision Berlin — V2.1 pixelweise verifizierte Bilder
const VISION_IMAGES = [
  {
    url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/3z5jnwh2_grafik.png",
    tag: "Die Linie · Signatur",
    aspect: "aspect-[21/9]",
    span: "col-span-12",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/jlvu3g5f_grafik.png",
    tag: "Solar · Blütentrichter",
    aspect: "aspect-[4/5]",
    span: "col-span-12 md:col-span-5",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/15ejn2t4_grafik.png",
    tag: "Lounge · Aufenthalt",
    aspect: "aspect-[4/5]",
    span: "col-span-12 md:col-span-7",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/o174bljv_grafik.png",
    tag: "Smart · Info-Stele",
    aspect: "aspect-[16/10]",
    span: "col-span-12 md:col-span-7",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/gckhhw15_grafik.png",
    tag: "Media · Stadtraum",
    aspect: "aspect-[16/10]",
    span: "col-span-12 md:col-span-5",
  },
];

export default function VisionBerlin() {
  const { lang } = useLang();
  const copy = {
    de: {
      kicker: "06 — Vision Berlin",
      title: "Eine Vision für den öffentlichen Raum von morgen.",
      note: "Konzeptvisualisierung · keine beschlossene Planung.",
    },
    en: {
      kicker: "06 — Vision Berlin",
      title: "A vision for the public space of tomorrow.",
      note: "Concept visualisation · not adopted planning.",
    },
  }[lang];

  return (
    <section
      id="vision-berlin"
      data-testid="section-vision-berlin"
      className="relative bg-[#16191D] py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Header — nur Headline, minimaler Text */}
        <div className="mb-16 md:mb-20 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8">
              <span className="h-px w-8 bg-[#F5D77A]" />
              {copy.kicker}
            </div>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[1] text-white"
              style={{ fontSize: "clamp(2.5rem, 6.5vw, 6rem)" }}
            >
              {copy.title}
            </h2>
          </motion.div>
        </div>

        {/* Große Bilder */}
        <div className="grid grid-cols-12 gap-4 md:gap-5">
          {VISION_IMAGES.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1.1, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`vision-image-${i}`}
              className={`relative overflow-hidden border border-white/8 ${img.span}`}
            >
              <div className={`relative ${img.aspect}`}>
                <img
                  src={img.url}
                  alt={img.tag}
                  className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-[1.03]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#16191D]/50 via-transparent to-transparent pointer-events-none" />
                <div className="absolute top-5 left-5 flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#F5D77A]/90 bg-black/45 backdrop-blur px-3 py-1.5 border border-white/10">
                  <span className="h-1 w-1 rounded-full bg-[#F5D77A]" />
                  {img.tag}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-8 text-[12px] text-white/40 italic max-w-2xl leading-relaxed border-l-2 border-[#F5D77A]/30 pl-5"
        >
          {copy.note}
        </motion.p>
      </div>
    </section>
  );
}
